/**
 * Portfolio Properties Repository
 *
 * The property list on a portfolio's page, one page at a time, from
 * GET /api/v2/portfolio-sets/{id}/properties: only the columns the list shows,
 * searched across the whole portfolio on the server. The v1 listing also
 * computes five open-item counts per property, which made each page take
 * seconds.
 *
 * A custom method rather than the generated CRUD because the list lives under
 * its portfolio. createRepository derives the state from the method name:
 * getPortfolioProperties gives `portfolioProperties`,
 * `portfolioPropertiesPagination`, `isLoadingPortfolioProperties` and
 * `portfolioPropertiesError`, and unwraps the Laravel paginator the endpoint
 * returns.
 */

import { createRepository } from 'data/utils/createRepository';
import { getApiUrl } from 'data/services/apiConstants';
import { get } from 'data/services/apiService';

/** Rows per page in the portfolio's property list. */
export const PORTFOLIO_PROPERTIES_PER_PAGE = 10;

/**
 * Builds the query string the listing accepts.
 *
 * @param {number} page
 * @param {number} perPage
 * @param {string} search Search term; omitted when empty.
 * @returns {string}
 */
export const listingQuery = (page = 1, perPage = PORTFOLIO_PROPERTIES_PER_PAGE, search = '') => {
	const params = new URLSearchParams({
		page: String(page || 1),
		per_page: String(perPage || PORTFOLIO_PROPERTIES_PER_PAGE),
	});

	const term = (search || '').trim();
	if (term) {
		params.set('search', term);
	}

	return params.toString();
};

/**
 * Number of the newest page request. The store keeps one page, so a response that arrives after a
 * newer request was sent must not replace the newer page (an earlier search or page answering last).
 */
let newestPageRequest = 0;

/** Never settles, so createRepository dispatches neither the superseded page nor its error. */
const superseded = () => new Promise(() => {});

const portfolioPropertiesRepository = createRepository({
	endpoint: 'v2/portfolio-sets',
	name: 'portfolioProperties',
	statePath: 'portfolioProperties',

	customMethods: {
		/**
		 * One page of a portfolio's properties. Only the newest request's response reaches the store.
		 * Usage: actions.getPortfolioProperties({ portfolioId, page, perPage, search })
		 */
		getPortfolioProperties: ({ portfolioId, page, perPage, search, authToken, dispatch }) => {
			const request = ++newestPageRequest;
			const isSuperseded = () => request !== newestPageRequest;

			return get({
				url: `${getApiUrl()}/api/v2/portfolio-sets/${portfolioId}/properties?${listingQuery(page, perPage, search)}`,
				authToken,
				dispatch,
			}).then(
				(response) => (isSuperseded() ? superseded() : response),
				(error) => (isSuperseded() ? superseded() : Promise.reject(error)),
			);
		},
	},
});

export const {
	statePath,
	thunks: portfolioPropertiesThunks,
	reducer: portfolioPropertiesReducer,
	actions: portfolioPropertiesActions,
	types: portfolioPropertiesTypes,
	selectors: portfolioPropertiesSelectors,
	useHook: usePortfolioProperties,
} = portfolioPropertiesRepository;

export default portfolioPropertiesRepository;
